import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { formatCurrency } from "utils/helpers/accounting";
import { ROUTES } from "utils";
import { useNavigate } from "react-router-dom";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const lastOfMonth = (() => {
  const d = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return formatDate(d);
})();

type HKDStateQuery = {
  from_date: string;
  to_date: string;
  data_method?: string;
};

const DATA_METHODS = [
  { value: "ACCRUAL", label: "Dồn tích (Accrual)" },
  { value: "CASH", label: "Tiền mặt (Cash)" },
];

const RpHKDState: React.FC = () => {
  const navigate = useNavigate();
  const { fetchRpHkdState } = useReport();
  const [filters, setFilters] = useState<HKDStateQuery>({
    from_date: firstOfMonth,
    to_date: lastOfMonth,
  });
  const [submitted, setSubmitted] = useState<HKDStateQuery | null>(null);

  const handleFilterChange = useCallback((key: keyof HKDStateQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) return;
      setSubmitted(filters);
      fetchRpHkdState.mutate({ from_date: filters.from_date, to_date: filters.to_date });
    },
    [fetchRpHkdState, filters],
  );

  const rawRecords = (fetchRpHkdState.data?.data?.data ?? fetchRpHkdState.data?.data ?? []) as any[];

  // Transform into financial position metrics
  const summaryRows = useMemo(() => {
    const totalRevenue = rawRecords
      .filter((r) => r.type === "REVENUE")
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const totalCost = rawRecords
      .filter((r) => r.type === "COST")
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const profit = totalRevenue - totalCost;
    const profitMargin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;

    return [
      { stt: 1, indicator: "Tổng doanh thu", description: "Tổng các khoản thu trong kỳ", value: totalRevenue, note: "" },
      { stt: 2, indicator: "Tổng chi phí", description: "Tổng các khoản chi trong kỳ", value: totalCost, note: "" },
      { stt: 3, indicator: "Lợi nhuận thuần", description: "Doanh thu - Chi phí", value: profit, note: profit >= 0 ? "Có lãi" : "Lỗ" },
      { stt: 4, indicator: "Tỷ suất lợi nhuận (%)", description: "Lợi nhuận / Doanh thu × 100", value: profitMargin, note: `${profitMargin.toFixed(2)}%`, isPercent: true },
    ];
  }, [rawRecords]);

  const columns = useMemo(
    () => [
      {
        headerName: "STT",
        field: "stt",
        pinned: "left",
        minWidth: 60,
        maxWidth: 70,
        cellClass: "text-center text-gray-500 text-sm",
      },
      {
        headerName: "Chỉ tiêu đánh giá",
        field: "indicator",
        minWidth: 220,
        cellClass: "font-medium text-gray-800",
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 280,
        flex: 1,
        cellClass: "text-gray-600 text-sm",
      },
      {
        headerName: "Giá trị chỉ tiêu",
        field: "value",
        minWidth: 160,
        valueFormatter: (p: any) => {
          const row = p.data;
          if (row?.isPercent) return `${Number(p.value).toFixed(2)}%`;
          return p.value != null ? formatCurrency(Number(p.value)) : "";
        },
        cellClass: (p: any) => {
          const v = Number(p.value);
          if (p.data?.indicator?.includes("Lợi nhuận") && !p.data?.isPercent) {
            return v >= 0 ? "text-right font-bold text-green-700" : "text-right font-bold text-red-600";
          }
          return "text-right font-semibold text-gray-800";
        },
      },
      {
        headerName: "Ghi chú",
        field: "note",
        minWidth: 130,
        cellClass: "text-gray-500 text-sm italic",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({ sortable: false, resizable: true, minWidth: 80 }),
    [],
  );

  const isLoading = fetchRpHkdState.isPending;

  return (
    <div className="p-6 space-y-5">
      {isLoading && <LoadingState message="Đang tải báo cáo" description="Vui lòng chờ trong giây lát." />}

      <nav className="text-sm text-blue-600 flex items-center gap-1 flex-wrap">
        <span className="cursor-pointer hover:underline font-medium" onClick={() => navigate(ROUTES.PRIVATE_ROOT.DASHBOARD)}>BÁO CÁO</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-500 cursor-pointer hover:underline">Sổ sách, báo cáo quản trị</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-700 font-medium">Tình hình hoạt động và tài chính</span>
      </nav>

      <h1 className="text-xl font-bold text-gray-900">Tình hình tài chính hộ kinh doanh</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Từ ngày <span className="text-red-500">(*)</span></label>
            <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Đến ngày <span className="text-red-500">(*)</span></label>
            <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="space-y-1 min-w-48">
            <label className="text-sm font-medium text-gray-700">Cách lấy dữ liệu</label>
            <select value={filters.data_method || ""} onChange={(e) => handleFilterChange("data_method", e.target.value || undefined)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
              <option value="">Lựa chọn...</option>
              {DATA_METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={isLoading}
            className="rounded border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
            Xem báo cáo
          </button>
          <button type="button" title="Xuất báo cáo"
            className="rounded border border-gray-300 p-2 text-sm text-gray-600 hover:bg-gray-50 transition">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        </div>
      </form>

      {/* KPI cards */}
      {submitted && summaryRows.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summaryRows.map((row) => {
            const isProfit = row.indicator.includes("Lợi nhuận");
            const positive = Number(row.value) >= 0;
            return (
              <div key={row.stt} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <p className="text-xs text-gray-500 mb-1">{row.indicator}</p>
                <p className={`text-lg font-bold ${isProfit ? (positive ? "text-green-700" : "text-red-600") : "text-gray-800"}`}>
                  {row.isPercent ? `${Number(row.value).toFixed(2)}%` : formatCurrency(Number(row.value))}
                </p>
                {row.note && <p className={`text-xs mt-1 ${isProfit && !positive ? "text-red-500" : "text-gray-400"}`}>{row.note}</p>}
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {submitted ? (
          <>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">
                Tình hình tài chính · {submitted.from_date} → {submitted.to_date}
              </h2>
            </div>
            <AgGridTable
              rows={summaryRows}
              columns={columns as any}
              defaultColDef={defaultColDef}
              pagination={false}
              autoHeight
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
            <p className="text-sm">Nhấn <strong>Xem báo cáo</strong> để phân tích tình hình tài chính</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RpHKDState;
